import { useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { nb } from "../../lib/journey";
import { LBL, cx } from "../../components/msd/ui";

// The group's WhatsApp invite link (saved when the group is created), with a
// Copy button so staff can paste it into any chat. If the browser blocks the
// clipboard (for example on a plain http site), the link text is selected so
// the person can copy it by hand.
// Once the WhatsApp group is deleted the link no longer opens anything, so it
// is not offered for copying.
export default function GroupLink({ link, deleted = false }) {
  const inputRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const url = !deleted && nb(link) ? String(link).trim() : "";

  const copy = async () => {
    setFailed(false);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      inputRef.current?.select();
      setFailed(true);
    }
  };

  return (
    <div data-testid="desk-group-link">
      <div className={LBL}>Group link</div>
      {url ? (
        <>
          <div className="mt-1 flex items-center gap-2">
            <input
              ref={inputRef}
              readOnly
              value={url}
              aria-label="Group link"
              onFocus={(e) => e.target.select()}
              className="h-9 min-w-0 flex-1 truncate rounded-lg border-0 bg-msd-soft px-2 text-[13px] text-msd-ink"
            />
            <button
              type="button"
              onClick={copy}
              data-testid="desk-group-link-copy"
              className={cx(
                "inline-flex h-9 shrink-0 items-center gap-1 rounded-lg border-0 px-3 text-[13px] font-semibold",
                copied ? "bg-msd-pine-soft text-msd-pine-ink" : "bg-msd-btn text-white",
              )}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          {failed && (
            <div className="mt-1 text-[12px] text-msd-brick-ink">Couldn't copy. The link is selected: press Ctrl+C to copy it.</div>
          )}
        </>
      ) : (
        <div className="mt-1 text-[13px] text-msd-faint">
          {deleted ? "The WhatsApp group was deleted, so this link no longer works." : "Link not ready yet. It appears once the group is created."}
        </div>
      )}
    </div>
  );
}
